import { z } from 'zod';
// Shapes a single-property offers request from a market search request. Used
// both by the direct property search and by each fanned-out per-property call.
export const toOffersRequest = (propertyId, request) => ({
    propertyId,
    arrival: request.arrival,
    departure: request.departure,
    adults: request.adults,
    ...(request.childrenAges && request.childrenAges.length > 0
        ? { childrenAges: request.childrenAges }
        : {}),
    ...(request.promoCode ? { promoCode: request.promoCode } : {}),
    ...(request.corporateCode ? { corporateCode: request.corporateCode } : {})
});
// ----------------------------------------------------------------------------
//  Response — the subset of the raw Apaleo offer (decorated with `isRestricted`
//  by sales-api) that the offer list renders. The offer is validated loosely
//  with `.passthrough()` so unused Apaleo fields flow through untouched and a
//  schema addition upstream never breaks the parse.
// ----------------------------------------------------------------------------
const MonetaryValueSchema = z.object({
    amount: z.number(),
    currency: z.string()
});
const AmountSchema = z.object({
    grossAmount: z.number(),
    netAmount: z.number(),
    currency: z.string()
});
const EmbeddedRatePlanSchema = z
    .object({
    id: z.string(),
    code: z.string().optional(),
    name: z.string().optional()
})
    .passthrough();
const EmbeddedUnitGroupSchema = z
    .object({
    id: z.string(),
    name: z.string().optional(),
    type: z.string().optional()
})
    .passthrough();
const OfferTimeSliceSchema = z
    .object({
    from: z.string(),
    to: z.string(),
    baseAmount: AmountSchema,
    totalAmount: AmountSchema.optional()
})
    .passthrough();
// `cancellationFee`/`noShowFee` carry a nested `fee` money object.
const OfferFeeSchema = z
    .object({
    name: z.string().optional(),
    dueDateTime: z.string().optional(),
    fee: MonetaryValueSchema
})
    .passthrough();
// The offer's `fees` array is a different Apaleo shape (`OfferFeeModel`): each
// fee carries a `totalAmount` (gross/net) and, per the Apaleo spec, is added on
// top of `totalGrossAmount` rather than being contained within it.
const OfferFeeItemSchema = z
    .object({
    id: z.string().optional(),
    code: z.string().optional(),
    name: z.string().optional(),
    totalAmount: AmountSchema
})
    .passthrough();
// Apaleo's per-VAT-type breakdown of the gross total. `tax` is the tax portion
// already contained in `totalGrossAmount`; we surface it so the quote rollup can
// split room charges from taxes without re-deriving any pricing.
const TaxDetailSchema = z
    .object({
    vatType: z.string().optional(),
    net: MonetaryValueSchema.optional(),
    tax: MonetaryValueSchema
})
    .passthrough();
// Apaleo's `OfferValidationMessageModel` carries only `code` and `message`;
// `category` is not part of the offers payload, so it must stay optional.
const ValidationMessageSchema = z.object({
    category: z.string().optional(),
    code: z.string(),
    message: z.string()
});
export const SalesOfferSchema = z
    .object({
    isRestricted: z.boolean(),
    validationMessages: z.array(ValidationMessageSchema).optional(),
    arrival: z.string(),
    departure: z.string(),
    minGuaranteeType: z.string().optional(),
    availableUnits: z.number(),
    unitGroup: EmbeddedUnitGroupSchema.optional(),
    ratePlan: EmbeddedRatePlanSchema.optional(),
    totalGrossAmount: MonetaryValueSchema.optional(),
    cancellationFee: OfferFeeSchema.optional(),
    fees: z.array(OfferFeeItemSchema).optional(),
    taxDetails: z.array(TaxDetailSchema).optional(),
    timeSlices: z.array(OfferTimeSliceSchema),
    isCorporate: z.boolean().optional()
})
    .passthrough();
export const SalesOffersResultSchema = z.object({
    property: z
        .object({
        id: z.string(),
        code: z.string().optional(),
        name: z.string().optional()
    })
        .passthrough()
        .optional(),
    offers: z.array(SalesOfferSchema)
});
